import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './css/News.css';
import QaNavBox from '../components/QaNavbox';
import { NewsListItem, NewsApiResponse } from '../components/NewsData';

const ITEMS_PER_PAGE = 10;

const News: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [newsItems, setNewsItems] = useState<NewsListItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [goToInput, setGoToInput] = useState('');

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`/news.php?page=${currentPage}&page_size=${ITEMS_PER_PAGE}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch');
                return res.json() as Promise<NewsApiResponse>;
            })
            .then(data => {
                setNewsItems(data.results);
                setTotalCount(data.count);
            })
            .catch(() => setError('Мэдээ татахад алдаа гарлаа. Дахин оролдоно уу.'))
            .finally(() => setLoading(false));
    }, [currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleGoToPage = () => {
        const page = parseInt(goToInput, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setGoToInput('');
        }
    };

    const getPageNumbers = () => {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        pages.push(1);
        if (currentPage > 3) pages.push('...');
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            pages.push(i);
        }
        if (currentPage < totalPages - 2) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="news-page-container">
            <QaNavBox />
            <div className="news-content-area">
                <div className="news-list-container">
                    {loading && <div className="news-loading">Уншиж байна...</div>}
                    {error && <div className="news-error">{error}</div>}
                    {!loading && !error && newsItems.map(news => (
                        <NewsCard key={news.id} news={news} />
                    ))}
                </div>
                {!loading && totalPages > 1 && (
                    <div className="news-pagination">
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            &laquo;
                        </button>
                        {getPageNumbers().map((page, i) =>
                            page === '...'
                                ? <span key={`ellipsis-${i}`} className="pagination-ellipsis">...</span>
                                : (
                                    <button
                                        key={page}
                                        className={`pagination-btn${currentPage === page ? ' active' : ''}`}
                                        onClick={() => handlePageChange(page as number)}
                                    >
                                        {page}
                                    </button>
                                )
                        )}
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            &raquo;
                        </button>
                        <div className="pagination-goto">
                            <input
                                type="number"
                                className="pagination-goto-input"
                                value={goToInput}
                                min={1}
                                max={totalPages}
                                placeholder="..."
                                onChange={e => setGoToInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleGoToPage()}
                            />
                            <button className="pagination-btn" onClick={handleGoToPage}>Go</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const NewsCard: React.FC<{ news: NewsListItem }> = ({ news }) => (
    <Link to={`/news/${news.id}`} className="news-item-card">
        <div className="news-item-content">
            <div className="news-item-text">
                <h3 className="news-item-title">{news.title}</h3>
                <p
                    className="news-item-excerpt"
                    dangerouslySetInnerHTML={{ __html: news.summary }}
                />
                <div className="news-item-readmore">Дэлгэрэнгүй</div>
            </div>
            <div className="news-item-image-wrapper">
                <img src={news.cover} alt={news.title} className="news-item-image" />
            </div>
        </div>
    </Link>
);

export default News;
